/**
 * The feed consumers — who receives outbound feeds.
 *
 * A list of delivery clients with an inline "add a client" form. Each row opens
 * the client's own page, where its assignments (transport + destination) and
 * per-feed transforms are managed. A client never holds a credential — only an
 * assignment does, and only as an opaque reference.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import {
  Button,
  Card,
  Empty,
  Field,
  FieldRow,
  Input,
  PageHead,
  Pill,
  Problem,
  Section,
  Table,
  Td,
  Th,
} from "@/components/kit";
import { createClientAction } from "@/lib/actions/delivery";
import { listDeliveryClients } from "@/lib/api/client";
import { useApi } from "@/spa/useApi";

function AddClient() {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    const result = await createClientAction({ code: code.trim(), name: name.trim() });
    setBusy(false);
    if (result) {
      setError(result.violations.length ? result.violations.join("; ") : result.error);
      return;
    }
    setCode("");
    setName("");
  }

  return (
    <Section
      title="Add a client"
      note="The code is the opaque identity a client reads feeds by — it never changes once in use."
    >
      <form onSubmit={submit}>
        <FieldRow>
          <Field label="Code" required hint="ndtv, news18, dailyhunt" htmlFor="c-code">
            <Input
              id="c-code"
              value={code}
              required
              placeholder="ndtv"
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Field label="Name" required htmlFor="c-name">
            <Input
              id="c-name"
              value={name}
              required
              placeholder="NDTV"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        </FieldRow>
        {error && (
          <p style={{ color: "var(--bad)", fontSize: "var(--t-sm)" }} role="alert">
            {error}
          </p>
        )}
        <div style={{ marginTop: "var(--s3)" }}>
          <Button tone="primary" type="submit" disabled={busy || !code.trim() || !name.trim()}>
            {busy ? "Adding…" : "Add client"}
          </Button>
        </div>
      </form>
    </Section>
  );
}

export function DeliveryClientsPage() {
  const { data: rows, loading, error } = useApi(listDeliveryClients, [], "failed to load");

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;

  return (
    <>
      <PageHead
        title="Delivery clients"
        note="The consumers of outbound feeds, and how each one is served."
      />

      {!rows ? (
        <Card>{loading ? "Loading…" : "No data."}</Card>
      ) : rows.length === 0 ? (
        <Card>
          <Empty
            title="No clients yet"
            note="Add the first feed consumer below — NDTV, News18, DailyHunt."
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Code</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <Td strong>{c.name}</Td>
                <Td quiet>{c.code}</Td>
                <Td>
                  <Pill tone={c.isActive ? "good" : "neutral"} dot>
                    {c.isActive ? "Active" : "Inactive"}
                  </Pill>
                </Td>
                <Td>
                  <Link to={`/delivery/clients/${c.id}`} style={{ fontSize: "var(--t-sm)" }}>
                    Manage
                  </Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <AddClient />
    </>
  );
}
